/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#reflowItems();
      };

      const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

      idleCallback(() => this.schedule(deferredReflow));
    } else {
      this.#reflowItems();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : requestAnimationFrame;
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
(() => {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
})();

class CasaProductCardColors extends HTMLElement {
  constructor() {
    super();
    this.moreButton = this.querySelector('.casa-product-card__more-colors');
    this.overflowContainer = this.querySelector('.casa-product-card__colors-overflow');
    this.colorButtons = this.querySelectorAll('.casa-product-card__color-swatch');
    this.card = this.closest('.casa-product-card');
    
    this.bindEvents();
  }
  
  bindEvents() {
    // Handle more colors button
    if (this.moreButton) {
      this.moreButton.addEventListener('click', this.toggleMoreColors.bind(this));
    }
    
    // Handle color swatch clicks
    this.colorButtons.forEach(button => {
      button.addEventListener('click', this.selectColor.bind(this));
    });
  }
  
  toggleMoreColors() {
    const isExpanded = this.moreButton.getAttribute('data-expanded') === 'true';
    
    if (!isExpanded) {
      this.overflowContainer.removeAttribute('hidden');
      this.moreButton.setAttribute('data-expanded', 'true');
      this.moreButton.style.display = 'none'; // Hide the button after clicking
    }
  }
  
  selectColor(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    
    // Update all color buttons pressed state
    this.colorButtons.forEach(btn => {
      btn.setAttribute('aria-pressed', btn === button ? 'true' : 'false');
    });
    
    // Update product image
    const image = this.card.querySelector('.casa-product-card__image');
    if (image && button.dataset.variantImage) {
      image.src = button.dataset.variantImage;
      image.srcset = button.dataset.variantImageSrcset;
      if (button.dataset.variantImageAlt) {
        image.alt = button.dataset.variantImageAlt;
      }
    }
    
    // Update product links
    const imageLink = this.card.querySelector('.casa-product-card__image-link');
    const titleLink = this.card.querySelector('.casa-product-card__title-link');
    const productUrl = this.dataset.productUrl;
    const variantUrl = `${productUrl}?variant=${variantId}`;
    
    if (imageLink) {
      imageLink.href = variantUrl;
    }
    if (titleLink) {
      titleLink.href = variantUrl;
    }
    
    // Update price
    const priceElement = this.card.querySelector('.casa-product-card__price');
    if (priceElement && button.dataset.variantPrice) {
      const price = parseInt(button.dataset.variantPrice);
      const priceFormatted = Math.floor(price / 100).toLocaleString('en-US'); // Format with commas
      priceElement.innerHTML = `$${priceFormatted}${this.getUnitsHtml()}`;
    }
    
    // Update wishlist variant ID
    const wishlistAction = this.card.querySelector('wishlist-action');
    if (wishlistAction) {
      wishlistAction.dataset.variantId = variantId;
    }
    
    // Update availability (if there's a button that depends on availability)
    const isAvailable = button.dataset.variantAvailable === 'true';
    const addButton = this.card.querySelector('.casa-product-card__add-button');
    if (addButton) {
      if (isAvailable) {
        addButton.removeAttribute('disabled');
      } else {
        addButton.setAttribute('disabled', '');
      }
    }

    // Update memo button variant ID
    const memoButton = this.card.querySelector('casa-add-memo-button');
    if (memoButton) {
      memoButton.dataset.variantId = variantId;
    }
  }
  
  getUnitsHtml() {
    // Try to preserve any units HTML that might be in the price
    const priceElement = this.card.querySelector('.casa-product-card__price');
    if (priceElement) {
      const match = priceElement.innerHTML.match(/(<[^>]+>.*<\/[^>]+>)$/);
      return match ? match[1] : '';
    }
    return '';
  }
}

customElements.define('casa-product-card-colors', CasaProductCardColors);

/**
 * Global utility to get cart section IDs that need to be refreshed
 * @returns {string[]} Array of section IDs
 */
window.getCartSectionIds = function() {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  return sectionIds;
};

/**
 * Custom element for "Add Memo to Cart" button
 * Finds the variant with matching color but "Order Stage" = "Memo"
 */
class CasaAddMemoButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.card = this.closest('.casa-product-card');

    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  async handleClick() {
    if (this.hasAttribute('data-disabled')) return;

    const button = this.button;
    const originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<span>Adding...</span>';

    try {
      // Get the currently selected variant from color picker or initial variant
      const colorPicker = this.card.querySelector('casa-product-card-colors');
      let selectedVariantId = this.dataset.variantId;

      if (colorPicker) {
        const selectedColorButton = colorPicker.querySelector('.casa-product-card__color-swatch[aria-pressed="true"]');
        if (selectedColorButton) {
          selectedVariantId = selectedColorButton.dataset.variantId;
        }
      }

      // Fetch product data to get all variants
      const productHandle = this.dataset.productHandle;
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();

      // Find the selected variant to get its color
      const selectedVariant = product.variants.find(v => v.id == selectedVariantId);
      if (!selectedVariant) throw new Error('Selected variant not found');

      // Find option indices
      let colorIndex = -1;
      let orderStageIndex = -1;

      // Handle both string[] and object[] formats for product.options
      product.options.forEach((option, index) => {
        // Extract option name - handle both 'string' and { name: 'string' } formats
        const optionName = typeof option === 'string' ? option : (option?.name || '');
        const optionLower = optionName.toLowerCase();

        if (optionLower === 'color' || optionLower === 'colour') {
          colorIndex = index;
        }
        if (optionLower === 'order stage') {
          orderStageIndex = index;
        }
      });

      if (colorIndex === -1) {
        // No color option, just use the selected variant
        await this.addToCart(selectedVariantId);
        return;
      }

      // Get the color value from the selected variant
      const selectedColor = selectedVariant.options[colorIndex];

      // Find variant with same color but "Order Stage" = "Memo"
      let memoVariant;

      if (orderStageIndex !== -1) {
        // Product has Order Stage option - find matching variant
        memoVariant = product.variants.find(v => {
          const sameColor = v.options[colorIndex] === selectedColor;
          const orderStageValue = v.options[orderStageIndex];
          const isMemo = orderStageValue && String(orderStageValue).toLowerCase() === 'memo';
          return sameColor && isMemo;
        });
      }

      // If no memo variant found, use the selected variant
      const variantToAdd = memoVariant || selectedVariant;

      if (!variantToAdd.available) {
        throw new Error('This variant is not available');
      }

      await this.addToCart(variantToAdd.id);

      button.innerHTML = '<span>Added!</span>';
      setTimeout(() => {
        button.innerHTML = originalHtml;
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding memo to cart:', error);
      button.innerHTML = '<span>Error</span>';
      setTimeout(() => {
        button.innerHTML = originalHtml;
        button.disabled = false;
      }, 2000);
    }
  }

  async addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    // Get cart section IDs to refresh
    const sectionIds = window.getCartSectionIds();
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    const data = await response.json();

    // Dispatch cart:update event with the same structure as CartAddEvent
    // This matches the structure in events.js:103-112
    const cartUpdateEvent = new CustomEvent('cart:update', {
      detail: {
        resource: data,
        sourceId: String(variantId),
        data: {
          source: 'casa-add-memo-button',
          itemCount: 1,
          sections: data.sections || {}
        }
      },
      bubbles: true
    });
    document.dispatchEvent(cartUpdateEvent);

    // Open cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

    return data;
  }
}

customElements.define('casa-add-memo-button', CasaAddMemoButton);

/**
 * Custom element for "Add Memo to Cart" button on PDP
 * Similar to CasaAddMemoButton but listens for variant:update events
 */
class CasaAddMemoButtonPdp extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');

    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }

    // Listen for variant updates from the variant picker
    this.variantUpdateHandler = this.handleVariantUpdate.bind(this);
  }

  connectedCallback() {
    // Listen for variant:update events at the document level
    document.addEventListener('variant:update', this.variantUpdateHandler);
  }

  disconnectedCallback() {
    // Clean up event listener when element is removed
    document.removeEventListener('variant:update', this.variantUpdateHandler);
  }

  handleVariantUpdate(event) {
    // Update the variant ID when variant changes
    const variant = event.detail?.resource;
    if (variant && variant.id) {
      this.dataset.variantId = variant.id;
    }
  }

  async handleClick() {
    if (this.hasAttribute('data-disabled')) return;

    const button = this.button;
    const originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<span>Adding...</span>';

    try {
      const selectedVariantId = this.dataset.variantId;

      // Fetch product data to get all variants
      // Get product handle from the current URL
      const pathParts = window.location.pathname.split('/');
      const productHandle = pathParts[pathParts.length - 1];
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();

      // Find the selected variant to get its options
      const selectedVariant = product.variants.find(v => v.id == selectedVariantId);
      if (!selectedVariant) throw new Error('Selected variant not found');

      // Find option indices
      let colorIndex = -1;
      let orderStageIndex = -1;
      let memoSizeIndex = -1;

      // Handle both string[] and object[] formats for product.options
      product.options.forEach((option, index) => {
        const optionName = typeof option === 'string' ? option : (option?.name || '');
        const optionLower = optionName.toLowerCase();

        if (optionLower === 'color' || optionLower === 'colour') {
          colorIndex = index;
        }
        if (optionLower === 'order stage') {
          orderStageIndex = index;
        }
        if (optionLower === 'memo size') {
          memoSizeIndex = index;
        }
      });

      if (colorIndex === -1) {
        // No color option, just use the selected variant
        await this.addToCart(selectedVariantId);
        return;
      }

      // Get the color value from the selected variant
      const selectedColor = selectedVariant.options[colorIndex];

      // Get the selected memo size if applicable
      const selectedMemoSize = this.hasAttribute('data-has-memo-size')
        ? this.dataset.selectedMemoSize
        : null;

      // Find variant with same color but "Order Stage" = "Memo" and matching Memo Size
      let memoVariant;

      if (orderStageIndex !== -1) {
        // Product has Order Stage option - find matching variant
        memoVariant = product.variants.find(v => {
          const sameColor = v.options[colorIndex] === selectedColor;
          const orderStageValue = v.options[orderStageIndex];
          const isMemo = orderStageValue && String(orderStageValue).toLowerCase() === 'memo';

          // Check memo size if applicable
          let correctMemoSize = true;
          if (selectedMemoSize && memoSizeIndex !== -1) {
            correctMemoSize = v.options[memoSizeIndex] === selectedMemoSize;
          }

          return sameColor && isMemo && correctMemoSize;
        });
      }

      // If no memo variant found, use the selected variant
      const variantToAdd = memoVariant || selectedVariant;

      if (!variantToAdd.available) {
        throw new Error('This variant is not available');
      }

      await this.addToCart(variantToAdd.id);
      button.innerHTML = originalHtml;
      button.disabled = false;
    } catch (error) {
      console.error('Error adding memo to cart:', error);
      button.innerHTML = '<span>Error</span>';
      setTimeout(() => {
        button.innerHTML = originalHtml;
        button.disabled = false;
      }, 2000);
    }
  }

  async addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    // Get cart section IDs to refresh
    const sectionIds = window.getCartSectionIds();
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    const data = await response.json();

    // Dispatch cart:update event with the same structure as CartAddEvent
    const cartUpdateEvent = new CustomEvent('cart:update', {
      detail: {
        resource: data,
        sourceId: String(variantId),
        data: {
          source: 'casa-add-memo-button-pdp',
          itemCount: 1,
          sections: data.sections || {}
        }
      },
      bubbles: true
    });
    document.dispatchEvent(cartUpdateEvent);

    // Open cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

    return data;
  }
}

customElements.define('casa-add-memo-button-pdp', CasaAddMemoButtonPdp);